import type { SSEEvent } from "../types/index.js";

/**
 * Parse a Server-Sent Events stream from a ReadableStreamDefaultReader.
 *
 * Implements a state-machine parser that handles:
 * - `event:`, `data:`, `id:` fields
 * - Multi-line `data:` (concatenated with newlines per SSE spec)
 * - Empty-line delimiters (`\n\n`) to dispatch events
 * - Flush on stream end (handles missing trailing newline on last event)
 * - Default event name "message" per the SSE specification
 */
export async function parseSSEStream(
	reader: ReadableStreamDefaultReader<Uint8Array>,
	onEvent: (event: SSEEvent) => void,
): Promise<void> {
	const decoder = new TextDecoder();
	let buffer = "";

	// Current event being assembled
	let currentEvent = "";
	let dataLines: string[] = [];

	function dispatchEvent(): void {
		// Only dispatch if we have at least one data line (per SSE spec)
		if (dataLines.length === 0) {
			// Reset event name even if no data — spec says reset on empty line
			currentEvent = "";
			return;
		}

		onEvent({
			event: currentEvent || "message",
			data: dataLines.join("\n"),
		});

		// Reset for the next event
		currentEvent = "";
		dataLines = [];
	}

	function processLine(line: string): void {
		// Empty line = event boundary
		if (line === "") {
			dispatchEvent();
			return;
		}

		// Lines starting with colon are comments — ignore
		if (line.startsWith(":")) {
			return;
		}

		// Parse field:value
		const colonIndex = line.indexOf(":");
		let field: string;
		let value: string;

		if (colonIndex === -1) {
			// Field with no value
			field = line;
			value = "";
		} else {
			field = line.slice(0, colonIndex);
			// If the character after the colon is a space, strip it (per SSE spec)
			value = line[colonIndex + 1] === " "
				? line.slice(colonIndex + 2)
				: line.slice(colonIndex + 1);
		}

		switch (field) {
			case "event":
				currentEvent = value;
				break;
			case "data":
				dataLines.push(value);
				break;
			case "id":
				// We expose the id in the event if needed; currently unused by our handlers
				break;
			default:
				// Unknown fields are ignored per SSE spec
				break;
		}
	}

	while (true) {
		const { done, value } = await reader.read();

		if (done) {
			// Flush any remaining buffered content
			if (buffer.length > 0) {
				processLine(buffer);
				buffer = "";
			}
			// Dispatch any partially assembled event (no trailing \n\n)
			dispatchEvent();
			break;
		}

		buffer += decoder.decode(value, { stream: true });

		// Process complete lines from the buffer
		const lines = buffer.split("\n");
		// Keep the last element — it may be an incomplete line
		buffer = lines.pop() ?? "";

		for (const line of lines) {
			// SSE uses \r\n or \n; strip trailing \r
			processLine(line.replace(/\r$/, ""));
		}
	}
}
